// PRD 08 §3.1 / §6 — click on the badge toggles active ↔ inactive immediately;
// no confirmation popup. Optimistic update + toast feedback are handled by
// `useToggleDiseaseCodeStatus`. The click stops propagation so the row's
// back-reference selection isn't triggered.
// PRD §7 (Req Clarification Q3) — toggling requires METADATA:WRITE.
//
// SHCT-582 (QC re-test) — PRD 21 v2.14 §7.4 **P-2** is `버튼 disabled + 툴팁`, 후속 동작 없음. This
// badge was the last control in the admin web that faked it: a <span role="button"> carrying
// `aria-disabled`, which still received clicks and answered each one with the P-3 toast. That put
// P-3's momentary refusal on an ordinary click, and left the control outside the reach of the L2
// guard, which can only recognise a real `disabled`.
//
// It is now a real <button> on BOTH paths. The permitted path loses nothing — it already declared
// `role="button"`, and a native button brings the Enter/Space handling the hand-rolled `onKeyDown`
// was reproducing. The badge classes carry the whole look; the inline resets below are only there
// because a <button> arrives with a UA border and font of its own.

import { useTranslation } from 'react-i18next';

import { toast } from '@/components/feedback/useToast';
import { useWriteDenyGuard } from '@/shared/hooks/useWriteGuard';

import { useToggleDiseaseCodeStatus } from '../hooks/use-disease-code-mutations';
import type { DiseaseCode } from '../types/disease-code.types';
import { getDiseaseCodeErrorMessage } from '../utils/error-message';

interface DiseaseCodeStatusToggleProps {
  code: DiseaseCode;
  canWrite: boolean;
}

// Neutralises the UA button chrome so `.badge` alone decides the appearance.
const BADGE_BUTTON_RESET: React.CSSProperties = {
  border: 'none',
  fontFamily: 'inherit',
  userSelect: 'none',
};

export function DiseaseCodeStatusToggle({ code, canWrite }: DiseaseCodeStatusToggleProps) {
  const { t } = useTranslation('admin');
  const toggleMutation = useToggleDiseaseCodeStatus();
  const { denyProps } = useWriteDenyGuard(canWrite);

  const className =
    code.status === 'ACTIVE' ? 'badge badge-success' : 'badge badge-secondary';
  const label = t(`diseaseCodeManagement.statuses.${code.status}`);

  const handleClick = async (e: React.MouseEvent) => {
    // The row underneath opens the back-reference panel; a toggle is not a row selection.
    e.stopPropagation();
    if (toggleMutation.isPending) return;
    const nextStatus = code.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      await toggleMutation.mutateAsync({
        id: code.id,
        groupId: code.groupId,
        nextStatus,
      });
      toast.success(t('diseaseCodeManagement.toasts.statusToggled'));
    } catch (err) {
      toast.error(
        getDiseaseCodeErrorMessage(err, t, 'diseaseCodeManagement.toasts.statusError'),
      );
    }
  };

  return (
    <button
      type="button"
      aria-pressed={code.status === 'ACTIVE'}
      disabled={!canWrite || toggleMutation.isPending}
      onClick={handleClick}
      className={className}
      style={{
        ...BADGE_BUTTON_RESET,
        cursor: !canWrite ? 'not-allowed' : toggleMutation.isPending ? 'wait' : 'pointer',
        opacity: canWrite ? undefined : 0.7,
      }}
      {...denyProps}
    >
      {label}
    </button>
  );
}
